import {pick} from 'lodash-es';
import {useEffect, useReducer, useState} from 'react';

import {featureService} from '@/services/featureService';
import type {
  AfriexFeature,
  AfriexFeatureLimitDraft,
  AfriexFeatureMeta,
} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';
import {toCapitalSeparated} from '@/utils/toCapitalSeparated';
import {FeatureValidator} from '@/validators/FeatureValidator';

import {FeatureActionType} from './types';
import updateFeatureReducer from './updateFeatureReducer';

// This hook's whole job is to manage a feature draft before it is validated
// and submitted (FeatureValidator is the actual draft -> wire boundary, in
// handleSubmit below), so its input is honestly typed as a draft too:
// `meta.limit` may be incomplete (AfriexFeatureLimitDraft) while the user is
// still filling in the Limit form fields. A real, complete AfriexFeature
// (what FeatureFormModal passes when editing an existing feature) already
// satisfies this looser shape.
export type AfriexFeatureDraft = Omit<Partial<AfriexFeature>, 'meta'> & {
  meta?: Omit<AfriexFeatureMeta, 'limit'> & {limit?: AfriexFeatureLimitDraft};
};

const DEFAULT_INITIAL_DATA: AfriexFeatureDraft = {
  meta: {
    flags: {
      isDeactivated: false,
    },
    tags: {},
    limit: {},
  },
};

const useFeatureUpdate = (
  onClose: VoidFunction,
  initialData: AfriexFeatureDraft = DEFAULT_INITIAL_DATA,
) => {
  const [feature, dispatch] = useReducer(
    updateFeatureReducer,
    initialData as AfriexFeature,
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch({
      type: FeatureActionType.UPDATE_FEATURE,
      payload: initialData as Partial<AfriexFeature>,
    });
  }, []);

  const handleSubmit = async (rawFeature: AfriexFeature) => {
    setIsLoading(true);
    try {
      const feature: AfriexFeature = {
        ...rawFeature,
        code:
          rawFeature?.code?.toUpperCase() ||
          toCapitalSeparated(rawFeature?.name || ''),
      };
      const validFeature = new FeatureValidator(feature).value();

      if (rawFeature.id) {
        const fieldsToInclude: (keyof AfriexFeature)[] = [
          'name',
          'code',
          'meta',
        ];
        await featureService.updateFeatureById(
          String(rawFeature.id),
          pick(validFeature, fieldsToInclude),
        );
        showSuccessMessage(`Feature flag item updated successfully`);
      } else {
        await featureService.createFeature(validFeature);
        showSuccessMessage(`Feature flag item saved successfully`);
      }

      onClose();

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e: unknown) {
      showErrorMessage(
        `Error saving feature flag item: ${getErrorMessage(e, String(e))}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setIsLoading(true);
    try {
      await featureService.removeFeatureById(id);
      showSuccessMessage('Feature deleted');

      onClose();

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e: unknown) {
      showErrorMessage(getErrorMessage(e, 'Failed to delete feature'));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    dispatch,
    handleSubmit,
    handleDelete,
    isLoading,
    feature,
  };
};

export default useFeatureUpdate;
